import React, { useState } from 'react';
import { Flag, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { reportsAPI } from '../services/api';

/**
 * ReportButton - Report inappropriate prompts
 * Opens modal with report reasons, submits to backend
 */
const ReportButton = ({ promptId, promptTitle, className = '' }) => {
  const { currentUser } = useAuth();
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const reportReasons = [
    {
      value: 'spam',
      label: 'Spam or Misleading',
      description: 'Promotional content, clickbait, or misleading information',
    },
    {
      value: 'inappropriate',
      label: 'Inappropriate Content',
      description: 'Offensive, explicit, or otherwise inappropriate content',
    },
    {
      value: 'plagiarism',
      label: 'Plagiarism',
      description: 'Content copied from another source without attribution',
    },
    {
      value: 'harassment',
      label: 'Harassment or Abuse',
      description: 'Content that harasses, bullies, or abuses others',
    },
    { value: 'other', label: 'Other', description: 'Other violations not covered above' },
  ];

  const handleReportClick = () => {
    if (!currentUser) {
      alert('Please sign in to report prompts');
      return;
    }
    setShowReportModal(true);
  };

  const handleSubmitReport = async () => {
    if (!selectedReason) {
      alert('Please select a reason for reporting');
      return;
    }

    if (selectedReason === 'other' && (!comment || comment.trim().length === 0)) {
      alert('Please provide details when selecting "Other"');
      return;
    }

    setSubmitting(true);

    try {
      await reportsAPI.report(promptId, {
        reason: selectedReason,
        comment: comment.trim() || null,
      });

      setSuccess(true);

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowReportModal(false);
        setSuccess(false);
        setSelectedReason('');
        setComment('');
      }, 2000);
    } catch (err) {
      console.error('Error submitting report:', err);
      alert(err.response?.data?.error || 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setShowReportModal(false);
      setSelectedReason('');
      setComment('');
      setSuccess(false);
    }
  };

  if (!currentUser) {
    return null; // Don't show report button to non-authenticated users
  }

  return (
    <>
      <button
        onClick={handleReportClick}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-red-100 dark:hover:bg-red-900 hover:text-red-600 dark:hover:text-red-300 transition-colors ${className}`}
        title="Report inappropriate content"
      >
        <Flag size={18} />
        <span className="text-sm font-medium">Report</span>
      </button>

      {/* Report Modal */}
      {showReportModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleClose}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {success ? (
              // Success State
              <div className="text-center py-8">
                <div className="mb-4 text-green-600 dark:text-green-400">
                  <AlertTriangle size={48} className="mx-auto" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Report Submitted
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Thank you for helping keep our community safe. Our moderation team will review this
                  report.
                </p>
              </div>
            ) : (
              // Report Form
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Report Content</h3>
                  <button
                    onClick={handleClose}
                    disabled={submitting}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                    aria-label="Close"
                  >
                    X
                  </button>
                </div>

                {promptTitle && (
                  <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Reporting:</div>
                    <div className="font-medium text-gray-900 dark:text-white truncate">
                      {promptTitle}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Why are you reporting this content?
                  </label>
                  <div className="space-y-2">
                    {reportReasons.map((reason) => (
                      <button
                        key={reason.value}
                        onClick={() => setSelectedReason(reason.value)}
                        disabled={submitting}
                        className={`w-full px-4 py-3 rounded-lg border text-left transition-colors ${
                          selectedReason === reason.value
                            ? 'border-red-600 bg-red-50 dark:bg-red-900 dark:border-red-500'
                            : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <div className="font-medium text-gray-900 dark:text-white mb-1">
                          {reason.label}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {reason.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Additional details{' '}
                    {selectedReason === 'other' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={submitting}
                    placeholder="Provide any additional context about this report..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                    rows={4}
                  />
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Optional for most reasons, required if selecting "Other"
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle
                      size={16}
                      className="text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0"
                    />
                    <div className="text-xs text-yellow-800 dark:text-yellow-200">
                      <strong>Note:</strong> Prompts with 3 or more reports are automatically hidden pending
                      review. False reports may result in account restrictions.
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitReport}
                    disabled={submitting || !selectedReason}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {submitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReportButton;
